import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  Text,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import {
  Eye20Regular,
  EyeOff20Regular,
} from '@fluentui/react-icons';

const MOBILE = '@media (max-width: 768px)';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required'),
});

type LoginFormData = {
  email: string;
  password: string;
};

const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-background)',
    padding: 'var(--space-lg)',
    [MOBILE]: {
      padding: 'var(--space-md)',
    },
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    padding: 'var(--space-2xl) var(--space-xl)',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-md)',
    [MOBILE]: {
      padding: 'var(--space-xl) var(--space-md)',
    },
  },
  header: {
    textAlign: 'center',
    marginBottom: 'var(--space-xl)',
  },
  logoImg: {
    height: '72px',
    width: 'auto',
    objectFit: 'contain',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: 'var(--space-md)',
  },
  fieldGroup: {
    marginBottom: 'var(--space-lg)',
  },
  label: {
    marginBottom: 'var(--space-xs)',
    display: 'block',
    color: 'var(--color-text)',
    fontWeight: '500',
    fontSize: '13px',
  },
  passwordWrap: {
    position: 'relative',
  },
  passwordToggle: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    minWidth: '32px',
    height: '32px',
  },
  errorField: {
    color: 'var(--color-error)',
    marginTop: 'var(--space-xs)',
    fontSize: '12px',
  },
  errorBanner: {
    backgroundColor: 'var(--color-error-light)',
    border: '1px solid #F5C6C6',
    borderRadius: 'var(--radius-sm)',
    padding: 'var(--space-sm) var(--space-md)',
    marginBottom: 'var(--space-lg)',
    color: 'var(--color-error)',
    fontSize: '13px',
  },
  submitBtn: {
    width: '100%',
    height: '44px',
    fontSize: '15px',
    fontWeight: '600',
  },
  footer: {
    textAlign: 'center',
    marginTop: 'var(--space-xl)',
    color: 'var(--color-text-tertiary)',
    fontSize: '12px',
  },
});

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const s = useStyles();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      setError('');
      await login(data.email, data.password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={s.page}>
      <div className={s.card}>
        <div className={s.header}>
          <img
            className={s.logoImg}
            src={`${process.env.PUBLIC_URL}/UNDP.png`}
            alt="UNDP"
          />
          <Text
            size={700}
            weight="semibold"
            as="h1"
            style={{ display: 'block', color: 'var(--color-text)', letterSpacing: '-0.3px', textAlign: 'center' }}
          >
            UNDP PIA Platform
          </Text>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={s.fieldGroup}>
            <label className={s.label}>Email</label>
            <Input
              {...register('email')}
              type="email"
              placeholder="Enter your email address"
              disabled={loading}
              autoFocus
              style={{ width: '100%' }}
              appearance="outline"
            />
            {errors.email && (
              <div className={s.errorField}>{errors.email.message}</div>
            )}
          </div>

          <div className={s.fieldGroup} style={{ marginBottom: 'var(--space-xl)' }}>
            <label className={s.label}>Password</label>
            <div className={s.passwordWrap}>
              <Input
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                disabled={loading}
                style={{ width: '100%', paddingRight: '40px' }}
                appearance="outline"
              />
              <Button
                appearance="subtle"
                icon={showPassword ? <EyeOff20Regular /> : <Eye20Regular />}
                onClick={() => setShowPassword(!showPassword)}
                className={s.passwordToggle}
                disabled={loading}
              />
            </div>
            {errors.password && (
              <div className={s.errorField}>{errors.password.message}</div>
            )}
          </div>

          {error && <div className={s.errorBanner}>{error}</div>}

          <Button
            type="submit"
            appearance="primary"
            disabled={loading}
            className={s.submitBtn}
          >
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Spinner size="tiny" />
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <div className={s.footer}>
          United Nations Development Programme
        </div>
      </div>
    </div>
  );
}
